import { Box, Stack, Typography } from '@mui/material'

import { C1Button } from 'c1ui/components/C1Button'
import { APIError } from 'components/core/errors/api'
import { SelectOneUserHook } from 'components/core/search/users'
import { C1TableGrid, SpacingVariants } from 'components/core/table-grid/C1TableGrid'
import { ProfileRow } from 'components/pages/profile/profile-row'
import { UserDetailsStyles } from 'components/pages/user/details/styles'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { UserValue } from 'lib/actions/user'
import { SetDelegatedUserRequest, UserStatus } from 'pbts/c1/api/user/v1/user'
import { FC, useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { setDelegatedUserApi } from 'redux_toolkit/users'

type DelegateUserDetailsProps = {
    userId: string
    delegatedUser: UserValue | undefined
}

export const DelegateUserDetails: FC<DelegateUserDetailsProps> = ({
    userId,
    delegatedUser: initialDelegatedUser,
}) => {
    const intl = useIntl()
    const [isEditable, setEditable] = useState<boolean>(false)
    const [delegatedUser, setDelegatedUser] = useState<UserValue | null>(initialDelegatedUser || null)

    const onSelectedUser = useCallback((value: UserValue | null): void => setDelegatedUser(value), [setDelegatedUser])
    const { mutate: updateDelegatedUser, error, inProgress } = setDelegatedUserApi()

    const onClickSave = useCallback(
        (): Promise<void> =>
            updateDelegatedUser(
                SetDelegatedUserRequest.create({
                    delegatedUserId: delegatedUser?.id || '',
                }),
            ).then(() => setEditable(false)).catch((e) => e),
        [delegatedUser, setEditable, updateDelegatedUser],
    )

    const delegatedButton = isEditable ? (
        <Box sx={UserDetailsStyles.buttonsContainer}>
            <Box sx={UserDetailsStyles.cancelButton}>
                <C1Button
                    variant="outlined"
                    onClick={(): void => {
                        setDelegatedUser(initialDelegatedUser || null)
                        setEditable(false)
                    }}
                    disabled={inProgress}
                >
                    <FormattedMessage id="components_delegated_user_cancel" defaultMessage="Cancel" />
                </C1Button>
            </Box>
            <Box>
                <C1Button
                    variant="contained"
                    onClick={onClickSave}
                    disabled={inProgress}
                >
                    <FormattedMessage id="components_delegated_user_save" defaultMessage="Save" />
                </C1Button>
            </Box>
        </Box>
    ) : (
        <C1Button variant="text" onClick={(): void => setEditable(true)}>
            <FormattedMessage id="components_delegated_user_edit" defaultMessage="Edit" />
        </C1Button>
    )

    return (
        <C1TableGrid
            title={<FormattedMessage id="out_of_office_delegate" defaultMessage="Out-of-office delegate" />}
            spacingVariant={SpacingVariants.compact}
            buttons={delegatedButton}
        >
            <Stack spacing={4} px={3}>
                {error && (
                    <Box>
                        <APIError
                            err={error}
                            title={
                                <FormattedMessage
                                    id="components_user_details_set_delegated_user_error"
                                    defaultMessage="Error setting delegated user"
                                />
                            }
                        />
                    </Box>
                )}
                <Typography variant="body1" my={0}>
                    <FormattedMessage
                        id="out_of_office_delegate_description"
                        defaultMessage="Assign a user to be delegated your tasks while you are out of office."
                    />
                </Typography>

                <ProfileRow label={intl.formatMessage({
                    id: 'user_details_delegate',
                    defaultMessage: 'Delegate',
                })}
                >
                    <Box width="100%">
                        {!isEditable &&
                        (delegatedUser ? (
                            <UserDisplayName user={delegatedUser} includeDetails={false} readOnly />
                        ) : (
                            <Typography sx={UserDetailsStyles.empty} noWrap variant="unconfigured">
                                <FormattedMessage id="delegated_user_empty_state" defaultMessage="None" />
                            </Typography>

                        ))}
                        {isEditable && (
                            <SelectOneUserHook
                                user={delegatedUser ?? undefined}
                                onSelected={onSelectedUser}
                                hideLabel
                                placeholder={intl.formatMessage({
                                    id: 'component_select_delegated_user_placeholder',
                                    defaultMessage: 'Search for a user',
                                })}
                                baseRequest={{
                                    excludeIds: [userId],
                                    userStatuses: [UserStatus.ENABLED],
                                }}
                            />
                        )}
                    </Box>
                </ProfileRow>

            </Stack>
        </C1TableGrid>
    )
}
